import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Session, User } from '@supabase/supabase-js';
import toast from 'react-hot-toast';
import { supabase } from './supabase';
import type { AppMetadataClaims, AuthStatus, ClinicMembershipClaim } from '@/types/auth';
import {
  permissionMapHas,
  type PermissionAction,
  type PermissionMap,
  type PermissionResource,
} from '@/lib/permissions';
import { clearIdleTimeoutOverride } from '@/lib/idleTimeoutOverride';
import {
  AUTH_INIT_TIMEOUT_MS,
  SIGN_IN_TIMEOUT_MS,
  clearDefaultAuthStorage,
  performSignOut,
} from '@/lib/performSignOut';
import { withTimeout } from '@/lib/withTimeout';
import {
  beginSignOutEpoch,
  bumpAuthEpoch,
  clearLastSignOutReason,
  clearSessionExpiredNotice,
  consumeLastSignOutReason,
  isSignOutStaleAgainstNewerSignIn,
  isStaleAuthSignOut,
  locationToReturnTo,
  persistLastSignOutReason,
  persistSessionExpiredNotice,
  persistSignInReturnTo,
  readAuthEpoch,
  readSignOutEpoch,
  resolveSignOutRedirect,
  restoreAuthEpoch,
  returnToAsLocation,
  type SignOutReason,
} from '@/lib/sessionExpiry';

/**
 * Decodes the payload segment of a JWT access token.
 * Used to read hook-baked claims that are not mirrored on auth.users.app_metadata.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    // Decode base64 to binary string, then convert to UTF-8
    const binaryString = atob(padded);
    const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
    const decoded = new TextDecoder().decode(bytes);

    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Reads JWT claims baked by `public.custom_access_token_hook` (FND-4).
 *
 * IMPORTANT: the hook writes into the **access token JWT**, not into
 * auth.users.raw_app_meta_data. For new users, session.user.app_metadata only
 * contains provider fields — clinic_memberships must be read from the JWT.
 */
function readClaims(session: Session | null): AppMetadataClaims {
  const fromUser = (session?.user?.app_metadata ?? {}) as Partial<AppMetadataClaims>;

  let raw = fromUser;
  if (session?.access_token) {
    const payload = decodeJwtPayload(session.access_token);
    const jwtMeta = payload?.app_metadata;
    if (jwtMeta && typeof jwtMeta === 'object' && !Array.isArray(jwtMeta)) {
      const fromJwt = jwtMeta as Partial<AppMetadataClaims>;
      const jwtHasBakedAt =
        typeof fromJwt.claims_baked_at === 'number' && fromJwt.claims_baked_at > 0;

      // Prefer hook-baked JWT claims whenever the access token hook has run.
      if (jwtHasBakedAt) {
        raw = { ...fromUser, ...fromJwt };
      }
    }
  }

  return {
    primary_role: raw.primary_role ?? 'clinic_user',
    is_platform_admin: Boolean(raw.is_platform_admin),
    is_main_clinic_admin: Boolean(raw.is_main_clinic_admin),
    primary_clinic_id: raw.primary_clinic_id ?? null,
    clinic_memberships: Array.isArray(raw.clinic_memberships) ? raw.clinic_memberships : [],
    claims_invalidated_at:
      typeof raw.claims_invalidated_at === 'number' ? raw.claims_invalidated_at : 0,
    claims_baked_at: typeof raw.claims_baked_at === 'number' ? raw.claims_baked_at : 0,
  };
}

interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  session: Session | null;
  claims: AppMetadataClaims;
  memberships: ClinicMembershipClaim[];
  /** False until JWT memberships are present or the DB fallback for this user has finished. */
  membershipsResolved: boolean;
  primaryClinicId: string | null;
  isPlatformAdmin: boolean;
  isMainClinicAdmin: boolean;
  isClinicAdmin: (clinicId: string) => boolean;
  isClinicMember: (clinicId: string) => boolean;
  hasPermission: (
    clinicId: string | null | undefined,
    resource: PermissionResource,
    action: PermissionAction,
  ) => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: (options?: SignOutOptions) => Promise<void>;
  sendPasswordReset: (email: string, redirectTo?: string) => Promise<void>;
  refreshSession: () => Promise<void>;
}

export interface SignOutOptions {
  reason?: SignOutReason;
  /** Override the post-logout route. `null` skips navigation. */
  redirectTo?: string | null;
  returnTo?: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [dbMemberships, setDbMemberships] = useState<ClinicMembershipClaim[]>([]);
  const [dbMembershipsUserId, setDbMembershipsUserId] = useState<string | null>(null);
  const [dbMembershipsLoaded, setDbMembershipsLoaded] = useState(false);
  const mountedRef = useRef(true);
  const intentionalSignOutRef = useRef(false);
  const signOutInFlightRef = useRef<Promise<void> | null>(null);
  const authCycleRef = useRef(0);
  const logoutCycleRef = useRef<number | null>(null);
  /** Auth epoch of the session this tab is holding. 0 until a sign-in is observed. */
  const sessionEpochRef = useRef(0);

  const clearLocalAuthState = useCallback(() => {
    setSession(null);
    setStatus('unauthenticated');
    setDbMemberships([]);
    setDbMembershipsUserId(null);
    setDbMembershipsLoaded(false);
  }, []);

  const clearCaches = useCallback(() => {
    try {
      void queryClient.cancelQueries();
    } catch {
      // ignore
    }
    try {
      queryClient.clear();
    } catch {
      // ignore
    }
  }, [queryClient]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const { data, error } = await withTimeout(supabase.auth.getSession(), AUTH_INIT_TIMEOUT_MS);
        if (!mountedRef.current) return;
        if (error) {
          console.error('auth: getSession failed', error);
        }
        if (data.session) {
          sessionEpochRef.current = readAuthEpoch();
        }
        setSession(data.session);
        setStatus(data.session ? 'authenticated' : 'unauthenticated');
      } catch (err) {
        console.error('auth: getSession timed out or failed', err);
        if (!mountedRef.current) return;
        clearLocalAuthState();
        clearCaches();
      }
    })();

    const { data: subscription } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mountedRef.current) return;

      if (event === 'SIGNED_OUT') {
        if (
          isStaleAuthSignOut(authCycleRef.current, logoutCycleRef.current) ||
          isSignOutStaleAgainstNewerSignIn()
        ) {
          // Hung logout, another tab, or a reload that reset in-memory cycles.
          // A newer sign-in already owns the session.
          return;
        }
        // Other tab / expired refresh token / server revoke.
        if (!intentionalSignOutRef.current) {
          const sharedReason = consumeLastSignOutReason() ?? 'expired';
          persistSessionExpiredNotice(sharedReason);
          persistSignInReturnTo(locationToReturnTo(window.location));
          toast.dismiss();
          clearCaches();
          const redirectTo = resolveSignOutRedirect(window.location.pathname);
          if (redirectTo) {
            navigate(redirectTo, {
              replace: true,
              state: {
                from: { pathname: window.location.pathname, search: window.location.search },
              },
            });
          }
        } else {
          clearLastSignOutReason();
        }
        intentionalSignOutRef.current = false;
        setSession(null);
        setStatus('unauthenticated');
        return;
      }

      if (event === 'SIGNED_IN') {
        authCycleRef.current += 1;
        sessionEpochRef.current = readAuthEpoch();
      }

      intentionalSignOutRef.current = false;
      setSession(nextSession);
      setStatus(nextSession ? 'authenticated' : 'unauthenticated');
    });

    return () => {
      subscription.subscription.unsubscribe();
    };
  }, [clearCaches, clearLocalAuthState, navigate]);

  const claims = useMemo(() => readClaims(session), [session]);

  const jwtMemberships = claims.clinic_memberships;

  // When JWT claims are empty, load memberships directly from DB (RLS allows
  // users to read their own clinic_memberships rows).
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId || jwtMemberships.length > 0) {
      setDbMemberships([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('clinic_memberships')
        .select('clinic_id, role, created_at')
        .eq('status', 'active')
        .order('created_at', { ascending: true });

      if (cancelled) return;
      if (error) {
        console.error('auth: bootstrap memberships failed', error);
        setDbMemberships([]);
        setDbMembershipsUserId(userId);
        setDbMembershipsLoaded(true);
        return;
      }

      setDbMemberships(
        (data ?? []).map((row) => ({
          clinic_id: row.clinic_id,
          role: row.role,
          is_accessible: true,
          is_active: true,
        })),
      );
      setDbMembershipsUserId(userId);
      setDbMembershipsLoaded(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, jwtMemberships.length]);

  const effectiveMemberships = jwtMemberships.length > 0 ? jwtMemberships : dbMemberships;

  const membershipsResolved =
    jwtMemberships.length > 0 ||
    !session?.user ||
    (dbMembershipsLoaded && dbMembershipsUserId === session.user.id);

  const effectivePrimaryClinicId =
    claims.primary_clinic_id ?? effectiveMemberships[0]?.clinic_id ?? null;

  const effectiveIsMainClinicAdmin =
    claims.is_main_clinic_admin || effectiveMemberships.some((m) => m.role === 'main_clinic_admin');

  // Stale-claim detection: refresh when JWT claims are missing
  // or older than the server. Covers signup/invite (membership created in the
  // same request as the first token) and later role-permission edits.
  const claimsRefreshAttemptedRef = useRef(false);
  useEffect(() => {
    // Allow a fresh refresh attempt when the signed-in user changes.
    claimsRefreshAttemptedRef.current = false;
  }, [session?.user?.id]);

  useEffect(() => {
    if (!session || claimsRefreshAttemptedRef.current) return;

    const missingMemberships = jwtMemberships.length === 0 && dbMemberships.length === 0;
    // Permission edits bump claims_invalidated_at. Refresh even when the
    // current token already has memberships, so a role grant shows up on reload.
    const needsRefresh =
      claims.claims_invalidated_at > claims.claims_baked_at ||
      (missingMemberships && claims.claims_baked_at === 0);

    if (!needsRefresh) return;

    claimsRefreshAttemptedRef.current = true;
    void supabase.auth.refreshSession().catch((err) => {
      console.error('auth: refreshSession failed', err);
      claimsRefreshAttemptedRef.current = false;
    });
  }, [
    claims.claims_baked_at,
    claims.claims_invalidated_at,
    claims.clinic_memberships.length,
    claims.is_platform_admin,
    dbMemberships.length,
    jwtMemberships.length,
    session,
  ]);

  const signIn = useCallback(async (email: string, password: string) => {
    // Claim the epoch before awaiting the in-flight logout or GoTrue, so that
    // logout cannot delete the session this sign-in is about to write.
    const previousEpoch = readAuthEpoch();
    const nextEpoch = bumpAuthEpoch();
    sessionEpochRef.current = nextEpoch;
    try {
      if (signOutInFlightRef.current) {
        await signOutInFlightRef.current.catch(() => undefined);
      }
      const { error } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        SIGN_IN_TIMEOUT_MS,
      );
      if (error) throw error;
      authCycleRef.current += 1;
      clearSessionExpiredNotice();
      clearLastSignOutReason();
      clearIdleTimeoutOverride();
      // Note: the custom_access_token_hook (FND-4) bakes claims at sign-in time.
      // If claims are missing the stale-claim detection below handles a single
      // safe refresh. We no longer do an unconditional second refresh here because
      // that created a token-rotation race condition for new clinic users.
    } catch (error) {
      if (readAuthEpoch() === nextEpoch) {
        restoreAuthEpoch(previousEpoch);
        sessionEpochRef.current = previousEpoch;
      }
      // A logout that started before this attempt may have been blocked from
      // clearing storage while the attempt held a newer epoch. Finish that
      // clear only when the logout is still the newest event.
      if (readSignOutEpoch() > readAuthEpoch()) {
        clearDefaultAuthStorage();
      }
      throw error;
    }
  }, []);

  // Note: there is no public signUp on this hook by design. Per MOD-1, account
  // creation goes through the `accept-invitation` Edge Function (token + chosen
  // password) — not via supabase.auth.signUp from the browser.

  const signOut = useCallback(
    async (options: SignOutOptions = {}) => {
      if (signOutInFlightRef.current) {
        return signOutInFlightRef.current;
      }

      // Another tab already signed in with a newer epoch. Do not call GoTrue
      // signOut — it would remove that session and broadcast SIGNED_OUT.
      if (beginSignOutEpoch(sessionEpochRef.current) === null) {
        return;
      }

      const reason: SignOutReason = options.reason ?? 'user';
      const returnTo = options.returnTo ?? locationToReturnTo(window.location);

      const run = async () => {
        persistLastSignOutReason(reason);
        persistSessionExpiredNotice(reason);
        if (reason !== 'user') {
          persistSignInReturnTo(returnTo);
          toast.dismiss();
        }

        // Unblock the UI before any network call. signOut must never hang the shell.
        logoutCycleRef.current = authCycleRef.current;
        intentionalSignOutRef.current = true;
        try {
          supabase.auth.stopAutoRefresh();
        } catch {
          // older clients / test mocks
        }
        clearLocalAuthState();
        clearCaches();

        const redirectTo =
          options.redirectTo === undefined
            ? resolveSignOutRedirect(window.location.pathname)
            : options.redirectTo;
        if (redirectTo) {
          navigate(redirectTo, {
            replace: true,
            state: { from: returnToAsLocation(returnTo) },
          });
        }

        await performSignOut({
          signOutRemote: async (scope) => {
            const { error } = await supabase.auth.signOut({ scope });
            return { error };
          },
          clearQueryCache: clearCaches,
          authGeneration: () => authCycleRef.current,
        });
      };

      const pending = run().finally(() => {
        signOutInFlightRef.current = null;
      });
      signOutInFlightRef.current = pending;
      return pending;
    },
    [clearCaches, clearLocalAuthState, navigate],
  );

  const sendPasswordReset = useCallback(async (email: string, redirectTo?: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (error) throw error;
  }, []);

  const refreshSession = useCallback(async () => {
    const { error } = await supabase.auth.refreshSession();
    if (error) throw error;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: session?.user ?? null,
      session,
      claims,
      memberships: effectiveMemberships,
      membershipsResolved,
      primaryClinicId: effectivePrimaryClinicId,
      isPlatformAdmin: claims.is_platform_admin,
      isMainClinicAdmin: effectiveIsMainClinicAdmin,
      isClinicMember: (clinicId: string) =>
        effectiveMemberships.some((m) => m.clinic_id === clinicId),
      isClinicAdmin: (clinicId: string) =>
        effectiveMemberships.some(
          (m) =>
            m.clinic_id === clinicId &&
            (m.role === 'clinic_admin' || m.role === 'main_clinic_admin'),
        ),
      hasPermission: (clinicId, resource, action) => {
        if (!clinicId) return false;
        if (claims.is_platform_admin) return true;
        const membership = effectiveMemberships.find((m) => m.clinic_id === clinicId);
        if (!membership) return false;
        const isAdminRole =
          membership.role === 'clinic_admin' || membership.role === 'main_clinic_admin';
        // Legacy admin JWTs omit the matrix (null/undefined = implicit all).
        // After the clinic_admin template is baked, use that map.
        if (
          isAdminRole &&
          (membership.permissions === null || membership.permissions === undefined)
        ) {
          return true;
        }
        if (membership.permissions === null) return true;
        if (membership.permissions === undefined) {
          if (
            resource === 'settings_users' ||
            resource === 'settings_roles' ||
            resource === 'settings_billing' ||
            resource === 'multi_clinic' ||
            resource === 'audit_logs'
          ) {
            return false;
          }
          return action !== 'delete';
        }
        return permissionMapHas(membership.permissions as PermissionMap, resource, action);
      },
      signIn,
      signOut,
      sendPasswordReset,
      refreshSession,
    }),
    [
      claims,
      session,
      signIn,
      signOut,
      sendPasswordReset,
      refreshSession,
      status,
      effectiveMemberships,
      membershipsResolved,
      effectivePrimaryClinicId,
      effectiveIsMainClinicAdmin,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

/** Signed-in user id, or null when the provider is not mounted. */
export function useOptionalUserId(): string | null {
  return useContext(AuthContext)?.user?.id ?? null;
}
